import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Download, ArrowLeft, FileText, XCircle, Loader } from 'lucide-react';
import { usePentest } from '../contexts/PentestContext';
import VulnerabilityItem from '../components/VulnerabilityItem';
import ScanAnimation from '../components/ScanAnimation';
import { getSafeUrl } from '../utils/safeUrl';

const PentestResultsPage = () => {
  const { pentestId: paramPentestId } = useParams();
  const navigate = useNavigate();
  const {
    pentestId,
    running,
    pentestProgress,
    pentestLogs,
    pentestStage,
    pentestPhase,
    findings,
    reportUrl,
    loadingFindings,
    openPentest,
    cancelPentest,
    cancelling,
    pentestStatus,
    findingsParsed,
    findingsFetchFailed,
    fetchFindings,
    error,
  } = usePentest();

  // Pentest findings keep their own expand state instead of sharing
  // ScanContext's, which belongs to the code-scan results page.
  const [expandedFindingId, setExpandedFindingId] = useState(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const isCurrentPentest = pentestId != null && String(paramPentestId) === String(pentestId);

  // When this page is opened directly (deep link, refresh, history row) the
  // context isn't tracking this id yet: load it, resuming polling if it is
  // still running.
  useEffect(() => {
    if (paramPentestId && !isCurrentPentest) {
      openPentest(paramPentestId);
    }
  }, [paramPentestId, isCurrentPentest, openPentest]);

  // Show the running animation while this pentest is actively polling.
  if (running && isCurrentPentest) {
    return (
      <div>
        <ScanAnimation
          progress={pentestProgress}
          logs={pentestLogs}
          stage={pentestStage}
          discoveredCount={findings.length}
          heading="Autonomous Pentest in Progress"
          subtitle="Shannon is dynamically testing your running application"
          progressLabel="Pentest Progress"
          targetPrefix="Phase"
          targetLabel={pentestPhase || null}
          queuePosition={null}
          standalone
          queuedHeading="Your pentest is in the queue"
        />
        {/* Cancel lives here rather than inside ScanAnimation, which is shared with the
            code-scan flow and has no cancel endpoint of its own. */}
        <div className="max-w-2xl mx-auto px-4 pb-10">
          {confirmingCancel ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-900">Cancel this pentest?</p>
              <p className="text-sm text-amber-800 mt-1">
                The scan engine stops and this run&apos;s progress is discarded — a pentest takes
                roughly 1&ndash;1.5 hours, so it would have to start from the beginning. A running
                engine takes a few seconds to shut down.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  type="button"
                  disabled={cancelling}
                  onClick={async () => {
                    await cancelPentest(paramPentestId);
                    setConfirmingCancel(false);
                  }}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg transition inline-flex items-center"
                >
                  {cancelling ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Cancelling…
                    </>
                  ) : (
                    'Yes, cancel it'
                  )}
                </button>
                <button
                  type="button"
                  disabled={cancelling}
                  onClick={() => setConfirmingCancel(false)}
                  className="text-gray-700 hover:text-gray-900 text-sm px-4 py-2"
                >
                  Keep running
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setConfirmingCancel(true)}
                className="text-sm text-gray-500 hover:text-red-600 underline transition-colors inline-flex items-center"
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                Cancel pentest
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const safeReportUrl = getSafeUrl(reportUrl);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-teal-600 hover:text-teal-700 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div className="min-w-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Pentest Results</h2>
            <p className="text-gray-600">
              Dynamic analysis findings located by URL, method and parameter.
            </p>
          </div>
          {safeReportUrl && (
            <a
              href={safeReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center"
            >
              <Download className="w-5 h-5 mr-2 flex-shrink-0" />
              Download report
            </a>
          )}
        </div>

        {error && (
          <div className="mb-6 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">{error}</div>
        )}

        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2 flex-shrink-0 text-teal-600" />
              Findings
            </h3>
            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">
              Total: {findings.length}
            </span>
          </div>

          <div className="space-y-2">
            {loadingFindings ? (
              <div className="text-center py-8 text-gray-500">Loading findings...</div>
            ) : findings.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {/* A cancelled or failed run proves nothing about the app. Saying only "no
                    findings" would read as "your app is clean", which is the one thing an
                    incomplete pentest must never imply. */}
                {findingsFetchFailed ? (
                  <>
                    <p>
                      The findings could not be loaded, so nothing is shown here.{' '}
                      <span className="font-medium">This is not a clean result.</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => fetchFindings(paramPentestId)}
                      className="mt-3 text-teal-600 hover:text-teal-700 underline text-sm"
                    >
                      Try again
                    </button>
                  </>
                ) : pentestStatus === 'cancelled' ? (
                  <p>
                    This pentest was cancelled before it finished, so it reported no findings.
                    That is not a clean result — run it again to actually test the application.
                  </p>
                ) : pentestStatus === 'failed' || pentestStatus === 'error' ? (
                  <p>
                    This pentest failed before it finished, so it reported no findings. That is
                    not a clean result — review the logs and run it again.
                  </p>
                ) : findingsParsed === false ? (
                  /* The run finished, but its machine-readable output could not be read, so we
                     learned nothing about the application. Saying "no findings" here would be
                     indistinguishable from a genuinely clean result. */
                  <p>
                    This pentest finished, but its results file could not be read, so no findings
                    were imported. <span className="font-medium">This is not a clean result.</span>{' '}
                    Download the report to see what the engine actually found.
                  </p>
                ) : (
                  <p>
                    No findings reported for this pentest — the engine completed without proving
                    any exploitable vulnerability.
                  </p>
                )}
              </div>
            ) : (
              findings.map((finding) => (
                <VulnerabilityItem
                  key={finding.id}
                  vulnerability={finding}
                  isExpanded={expandedFindingId === finding.id}
                  onToggle={() => setExpandedFindingId((prev) => (prev === finding.id ? null : finding.id))}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PentestResultsPage;
